/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React from "react";
import { Box, Divider, Tooltip, Typography, tooltipClasses } from "@mui/material";
import { styled } from "@mui/material/styles";
import { formatMoney, formatPercent } from "../../../utils/perqReportDrilldown";

const ACCENT = "#f97316";
const HIGHLIGHT_TEXT = "#c2410c";
const MUTED_TEXT = "#71717a";
const NEUTRAL_VALUE = "—";

const PERQ_CHILD_ROWS = ["managed", "unmanaged", "unclassified"];

const VIEWPORT_PADDING = 8;
// MUI's mouse gap from the icon (14px) and its margin on the other sides (2px). By touch MUI uses
// 24px; the equal margin below makes it 14px there too.
const TOOLTIP_GAP = 14;
const MUI_SIDE_MARGIN = 2;

// Opens beside the icon when there's no room below, then slides to stay inside the window.
const POPPER_MODIFIERS = [
  // Keeps the tooltip aligned with the icon as before; when it slides to fit, it sits 12px further in.
  { name: "offset", options: { offset: [MUI_SIDE_MARGIN - TOOLTIP_GAP, 0] } },
  {
    name: "flip",
    options: {
      fallbackPlacements: ["right-start", "left-start", "top-start"],
      padding: VIEWPORT_PADDING,
    },
  },
  {
    name: "preventOverflow",
    options: { altAxis: true, tether: false, padding: VIEWPORT_PADDING },
  },
];

// Fixed, so a popper that briefly runs past the window cannot make the page scrollable.
// Tooltip only reads popperOptions from PopperProps; slotProps.popper.popperOptions is overwritten.
const POPPER_PROPS = { popperOptions: { strategy: "fixed" } };

export const ReceiptTooltip = styled(({ className, ...props }) => (
  <Tooltip
    {...props}
    classes={{ popper: className }}
    PopperProps={POPPER_PROPS}
    // The popper is portalled, but React still bubbles its clicks to the widget card.
    slotProps={{
      popper: { modifiers: POPPER_MODIFIERS, onClick: (event) => event.stopPropagation() },
    }}
  />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: "#18181b",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
    border: "1px solid #eee",
    borderRadius: "10px",
    padding: "14px",
    boxSizing: "border-box",
    width: "330px",
    maxWidth: `calc(100vw - ${(VIEWPORT_PADDING + TOOLTIP_GAP) * 2}px)`,
    maxHeight: `calc(100vh - ${(VIEWPORT_PADDING + TOOLTIP_GAP) * 2}px)`,
    overflowY: "auto",
  },
  // MUI's margin depends on placement, so the box changed size as it flipped and kept flipping.
  // An equal margin keeps the size fixed and stays inside the popper, so hovering into it works.
  [`&.${tooltipClasses.popper}[data-popper-placement] .${tooltipClasses.tooltip}`]: {
    margin: TOOLTIP_GAP,
  },
}));

const toCents = (value) => Math.round((Number(value) || 0) * 100);

// direction is -1 for a line taken off and +1 for a line added; a negative value flips the sign.
const formatSigned = (value, direction) => {
  const cents = toCents(value) * direction;
  if (cents === 0) return formatMoney(0);
  return `${cents < 0 ? "−" : "+"}${formatMoney(Math.abs(cents) / 100)}`;
};

// Final total is the checks' own total, not a sum of the lines above it, so any
// difference gets its own line and the receipt always adds up.
const ADDED_ROWS = ["net_total", "tax_total", "tip_total", "gratuity_total"];
export const adjustmentCents = (data) =>
  toCents(data?.orders_total) - ADDED_ROWS.reduce((sum, key) => sum + toCents(data?.[key]), 0);

// Total perq amount is the orders' own discount total. The dashboard's three perq lines count
// only discounts tied to an active configured discount, so it shows the rest on its own line.
const otherDiscountCents = (data) =>
  toCents(data?.discount_total) -
  PERQ_CHILD_ROWS.reduce((sum, rowKey) => sum + toCents(data?.[`${rowKey}_total`]), 0);

const RECEIPT_ROWS = {
  gross_sales: {
    label: "Pre-discount subtotal",
    bold: true,
    getValue: (data) => formatMoney(data?.gross_sales),
    describe: (data, unavailable) => {
      const count = data?.orders_count ?? 0;
      const orders = unavailable
        ? "the filtered orders"
        : `all ${count} ${count === 1 ? "order" : "orders"}`;
      return `The combined item value of ${orders} before any perq or discount is taken off. Every other line in this breakdown starts from this number.`;
    },
  },
  discount_total: {
    label: "Total perq amount",
    bold: true,
    getValue: (data) => formatSigned(data?.discount_total, -1),
    describe: (data, unavailable) =>
      `Everything taken off the subtotal this period: managed perqs, unmanaged discounts and unclassified discounts combined.${
        unavailable ? "" : ` It works out to ${formatPercent(data?.discount_percent)} of gross sales.`
      }`,
  },
  managed: {
    label: "Managed perqs",
    getValue: (data) => formatSigned(data?.managed_total, -1),
    describe: () =>
      "Perqs given through a configured perq program, so each one is tied to a known employee or guest and tracked against their allowance.",
  },
  unmanaged: {
    label: "Unmanaged discount",
    getValue: (data) => formatSigned(data?.unmanaged_total, -1),
    describe: () =>
      "Known discounts, such as happy hour or manager comps, that the restaurant has chosen not to track as perqs.",
  },
  unclassified: {
    label: "Unclassified discount",
    getValue: (data) => formatSigned(data?.unclassified_total, -1),
    describe: () =>
      "Discounts not yet marked as managed or unmanaged. Classifying them keeps perq tracking accurate.",
  },
  other_discounts: {
    label: "Other discounts",
    getValue: (data) => formatSigned(otherDiscountCents(data) / 100, -1),
  },
  net_total: {
    label: "Net amount",
    hint: "(sub-total)",
    bold: true,
    getValue: (data) => formatMoney(data?.net_total),
  },
  tax_total: {
    label: "Tax",
    getValue: (data) => formatSigned(data?.tax_total, 1),
  },
  tip_total: {
    label: "Tip",
    getValue: (data) => formatSigned(data?.tip_total, 1),
  },
  gratuity_total: {
    label: "Gratuity",
    getValue: (data) => formatSigned(data?.gratuity_total, 1),
  },
  adjustments: {
    label: "Other adjustments",
    getValue: (data) => formatSigned(adjustmentCents(data) / 100, 1),
  },
  orders_total: {
    label: "Final total",
    bold: true,
    getValue: (data) => formatMoney(data?.orders_total),
  },
};

// Plain-text definition for the widget's info icon, so screen readers keep it.
export const receiptDescription = (rowKey, data, unavailable = false) =>
  RECEIPT_ROWS[rowKey]?.describe?.(data, unavailable) ?? "";

const ReceiptRow = ({ rowKey, data, highlighted, unavailable, child = false }) => {
  const row = RECEIPT_ROWS[rowKey];
  const emphasis = row.bold || highlighted;

  return (
    <Box
      data-testid={`perq-receipt-row-${rowKey}`}
      data-highlighted={highlighted ? "true" : undefined}
      sx={{
        position: "relative",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: "12px",
        padding: "4px 8px",
        borderRadius: "6px",
        border: `1px solid ${highlighted ? "#fdba8c" : "transparent"}`,
        backgroundColor: highlighted ? "#fff3ed" : "transparent",
        color: highlighted ? HIGHLIGHT_TEXT : child ? "#52525b" : "#18181b",
        fontSize: "13px",
        fontWeight: emphasis ? 600 : 400,
        ...(child && {
          "&::before": {
            content: '""',
            position: "absolute",
            left: "-9px",
            top: "50%",
            width: "8px",
            borderTop: "1px solid #d4d4d8",
          },
        }),
      }}
    >
      <span>
        {row.label}
        {row.hint ? (
          <Box component="span" sx={{ color: MUTED_TEXT, fontWeight: 400, ml: "4px" }}>
            {row.hint}
          </Box>
        ) : null}
      </span>
      <span>{unavailable ? NEUTRAL_VALUE : row.getValue(data)}</span>
    </Box>
  );
};

/**
 * Receipt-style breakdown of the summary totals, with the row behind the hovered
 * widget highlighted and explained underneath. `description` overrides the row's own text.
 * `showOtherDiscounts` adds a line for the part of Total perq amount the three perq lines leave out.
 */
export const SummaryReceipt = ({
  title,
  highlightRow,
  data = {},
  unavailable = false,
  description,
  showOtherDiscounts = false,
}) => {
  const rowProps = (rowKey) => ({
    rowKey,
    data,
    unavailable,
    highlighted: rowKey === highlightRow,
  });
  const highlighted = RECEIPT_ROWS[highlightRow];
  const descriptionText = description ?? highlighted?.describe?.(data, unavailable);

  return (
    <Box data-testid="perq-summary-receipt">
      <Typography sx={{ fontSize: "16px", fontWeight: 700, mb: "8px" }}>{title}</Typography>

      <Box
        data-testid="perq-receipt-row-orders_count"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          padding: "5px 8px",
          mb: "6px",
          borderRadius: "6px",
          backgroundColor: "#f4f4f5",
          fontSize: "13px",
        }}
      >
        <Box component="span" sx={{ color: "#52525b" }}>Orders</Box>
        <Box component="span" sx={{ fontWeight: 600 }}>
          {unavailable ? NEUTRAL_VALUE : data?.orders_count ?? 0}
        </Box>
      </Box>

      <ReceiptRow {...rowProps("gross_sales")} />
      <ReceiptRow {...rowProps("discount_total")} />
      <Box sx={{ ml: "16px", pl: "8px", borderLeft: "1px solid #d4d4d8" }}>
        {PERQ_CHILD_ROWS.map((rowKey) => (
          <ReceiptRow key={rowKey} child {...rowProps(rowKey)} />
        ))}
        {showOtherDiscounts && !unavailable && otherDiscountCents(data) !== 0 ? (
          <ReceiptRow child {...rowProps("other_discounts")} />
        ) : null}
      </Box>

      <Divider sx={{ my: "8px" }} />
      <ReceiptRow {...rowProps("net_total")} />
      <ReceiptRow {...rowProps("tax_total")} />
      <ReceiptRow {...rowProps("tip_total")} />
      <ReceiptRow {...rowProps("gratuity_total")} />
      {!unavailable && adjustmentCents(data) !== 0 ? (
        <ReceiptRow {...rowProps("adjustments")} />
      ) : null}

      <Divider sx={{ my: "8px" }} />
      <ReceiptRow {...rowProps("orders_total")} />

      {highlighted && descriptionText ? (
        <Box
          data-testid="perq-receipt-description"
          sx={{
            display: "flex",
            gap: "8px",
            mt: "12px",
            padding: "10px 12px",
            borderRadius: "8px",
            backgroundColor: "#f8f8f9",
          }}
        >
          <Box
            sx={{
              flexShrink: 0,
              width: "6px",
              height: "6px",
              mt: "6px",
              borderRadius: "50%",
              backgroundColor: ACCENT,
            }}
          />
          <Box>
            <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>{highlighted.label}</Typography>
            <Typography sx={{ fontSize: "12px", color: "#52525b", lineHeight: 1.45 }}>
              {descriptionText}
            </Typography>
          </Box>
        </Box>
      ) : null}
    </Box>
  );
};
